import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { breakpoints } from 'settings/style';
import { useMediaQuery } from 'hooks/useMediaQuery';
import {
  ArrowRightOutlined,
  CloseCircleOutlined,
  CustomerServiceOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { RouteComponentProps } from 'react-router-dom';
import {
  BookingStatusEnum,
  EventTypeEnum,
  RecurrenceTypeEnum,
  VirtualMeetingTypeEnum,
  namedOperations,
  useGetEventByIdQuery,
  useUploadEventMediaMutation,
  useGetAllEventsQuery,
  useApproveEventPhotoMutation,
  useRemoveEventPhotoMutation,
  useEndMeetingLazyQuery,
  useUpdateEventAtOnceMutation,
  ComponentMediaGallery,
} from 'graphql/types';
import { requestGoogleAccessToken, GOOGLE_CALENDAR_EVENTS_SCOPE } from 'helpers/googleIdentity';
import { createGoogleMeetEvent, resolveMeetingWindow } from 'helpers/googleMeet';
import { Button, InvitationModal, MeetingModal, Modal, PeopleListCard, SliderList } from 'components/elements';
import SocialMedia from 'components/elements/socialMedia/socialMedia';
import { formatMoney, ResizeImageResult } from 'helpers';
import { message } from 'antd';
import useEventAccessInfo from 'hooks/useEventAccessInfo';
import EventCard from 'components/eventCard';
import { FileInfoInput } from 'components/elements/imageInput';
import ReactHtmlParser from 'react-html-parser';
import moment from 'moment-timezone';
import { formatEventTimezoneDisplay } from 'utils/eventDateTime';
import { isEventType } from 'graphql/customTypes';
import RsvpEventForm from './singleEventHead/rsvpEventForm';
import DonateForm from 'components/donateForm';
import DailyMeetingNotes from './dailyMeetingNotes';

import {
  AddressLine,
  BackButton,
  DateArrow,
  DatePill,
  DatePillDate,
  DatePillTime,
  DateTimeRow,
  DateTimeSection,
  DateTimeZoneNote,
  DetailLabel,
  DetailRow,
  DetailValue,
  DividerCard,
  EventDetailsCard,
  HeroActionButton,
  HeroCenteredJoin,
  HeroActionLink,
  HeroActions,
  HeroCard,
  HeroInfo,
  HeroJoinDisabled,
  HeroMeta,
  HeroTitle,
  LeftColumn,
  LocationBlock,
  LocationPin,
  LocationText,
  MainGrid,
  MobileGrid,
  PageLayout,
  PageWrapper,
  RegistrationPanel,
  RightColumn,
  RichText,
  SecondaryDatePill,
  SecondaryDatePillDate,
  SecondaryDatePillTime,
  SecondaryDateTimeRow,
  SectionTitle,
  SectionTitleRow,
  SideTitle,
  StatRows,
  SurfaceCard,
  VenueName,
} from './index.styled';
import SingleEventAlbum from './singleEventAlbum';

type MatchParams = { id: string };
type SingleEventProps = RouteComponentProps<MatchParams>;

/** How often the live-meeting check re-runs so the join CTA opens without a reload. */
const LIVE_CHECK_INTERVAL_MS = 30000;

/** Attendees can join this many minutes ahead of the start time. */
const JOIN_LEAD_MINUTES = 5;

/** Attendee and invitee lists collapse past this many rows. */
const PEOPLE_LIST_PREVIEW_COUNT = 10;

const DATE_FORMAT = 'MMM DD YYYY';
const TIME_FORMAT = 'HH:mm';

// const fakeUsers = [...Array(5)].fill({ firstname: 'Test', lastname: 'User', id: Math.random().toString() });

export default function SingleEvent(props: SingleEventProps) {
  const {
    history,
    match: {
      params: { id },
    },
  } = props;

  const { data: eventByIdResult } = useGetEventByIdQuery({
    variables: { id },
  });
  const [removeEventPhoto, { loading: removePhotoLoading }] = useRemoveEventPhotoMutation();
  const [approveEventPhoto] = useApproveEventPhotoMutation();
  const [uploadEventPhoto, { loading: uploadPhotoLoading }] = useUploadEventMediaMutation();

  const { hasActiveBooking, isEventOrganizer, userId } = useEventAccessInfo(id);
  const isEditableEvent = hasActiveBooking || isEventOrganizer;
  // const { isEventOrganizer, userId } = useEventAccessInfo(id);
  // const isEditableEvent = true;

  const eventsResult = useGetAllEventsQuery({ variables: { pagination: { start: 0, limit: 10 } } });

  const uploadPhoto = useCallback(
    (data: ResizeImageResult, extraInfo: FileInfoInput) => {
      if (isEditableEvent) {
        try {
          uploadEventPhoto({
            variables: { input: { image: data?.blob, ...extraInfo }, eventId: id },
          });
        } catch (err) {
          if (err instanceof Error) {
            message.error(err.message);
          }
        }
      }
    },
    [id, isEditableEvent, uploadEventPhoto],
  );

  const removePhoto = useCallback(
    (mediaId: string) => {
      if (isEditableEvent) {
        try {
          removeEventPhoto({ variables: { mediaId, eventId: id } });
        } catch (err) {
          if (err instanceof Error) {
            message.error(err.message);
          }
        }
      }
    },
    [id, isEditableEvent, removeEventPhoto],
  );

  const approvePhoto = useCallback(
    (mediaId: string) => {
      if (isEditableEvent) {
        try {
          approveEventPhoto({ variables: { mediaId, eventId: id } });
        } catch (err) {
          if (err instanceof Error) {
            message.error(err.message);
          }
        }
      }
    },
    [id, isEditableEvent, approveEventPhoto],
  );

  const bookedPeople = useMemo(() => {
    const bookings = eventByIdResult?.event?.data?.attributes?.bookings?.data || [];
    return bookings
      .map((booking) => {
        const user = booking?.attributes?.user?.data;
        const visitor = booking?.attributes?.visitor?.data;
        const status = booking?.attributes?.status;

        if (user?.id) {
          return { id: user.id, ...user.attributes, status, isVisitor: false };
        }

        if (visitor?.id) {
          return {
            id: visitor.id,
            email: visitor.attributes?.email,
            status,
            isVisitor: true,
          };
        }

        return null;
      })
      .filter(Boolean) as {
        id: string;
        firstname?: string;
        lastname?: string;
        username?: string;
        avatarUrl?: string;
        email?: string;
        status?: BookingStatusEnum;
        isVisitor?: boolean;
      }[];
  }, [eventByIdResult]);

  /** Approved bookings hold a seat; pending ones are still awaiting the organizer's decision. */
  const attendants = useMemo(
    () => bookedPeople.filter((person) => person.status === BookingStatusEnum.Approved),
    [bookedPeople],
  );

  const invitees = useMemo(
    () => bookedPeople.filter((person) => person.status === BookingStatusEnum.Pending),
    [bookedPeople],
  );

  const event = eventByIdResult?.event?.data;
  const eventAttrs = event?.attributes;
  const eventTimezone = eventAttrs?.timezone;
  const eventTimezoneDisplay = useMemo(
    () => formatEventTimezoneDisplay(eventTimezone, eventAttrs?.startTime),
    [eventTimezone, eventAttrs],
  );
  const addr = eventAttrs?.address?.data?.attributes;
  const groupName = eventAttrs?.group?.data?.attributes?.name;
  const location = addr?.city || 'Online';
  const isVirtualOnly =
    isEventType(EventTypeEnum.Virtual, eventAttrs?.eventType) &&
    !isEventType(EventTypeEnum.Physical, eventAttrs?.eventType) &&
    !isEventType(EventTypeEnum.Both, eventAttrs?.eventType);

  const venueName = useMemo(() => {
    if (isVirtualOnly) {
      return 'Online';
    }
    const parts = [groupName, addr?.city].filter(Boolean);
    if (parts.length) {
      return parts.join(', ');
    }
    return addr?.city || addr?.address?.split?.(',')[0]?.trim() || 'Location TBD';
  }, [addr, groupName, isVirtualOnly]);

  const addressLine = useMemo(() => {
    if (isVirtualOnly) {
      return eventAttrs?.eventUrl ? 'Join link shared after registration' : 'Virtual event';
    }
    const line = [addr?.address, [addr?.city, addr?.country].filter(Boolean).join(', ')].filter(Boolean).join(', ');
    return line || '';
  }, [addr, eventAttrs, isVirtualOnly]);

  const recurrence = useMemo(() => {
    const recurrenceType = eventAttrs?.recurrenceType;
    const interval = Number(eventAttrs?.repeatInterval) || 0;

    // Legacy events stored the pattern in repeatInterval before recurrenceType existed.
    if (recurrenceType === RecurrenceTypeEnum.Weekdays || interval === 100) {
      return { label: 'Weekdays', pattern: RecurrenceTypeEnum.Weekdays, intervalDays: 1, isRecurring: true };
    }

    if (recurrenceType === RecurrenceTypeEnum.Weekends || interval === 101) {
      return { label: 'Weekends', pattern: RecurrenceTypeEnum.Weekends, intervalDays: 1, isRecurring: true };
    }

    if (recurrenceType === RecurrenceTypeEnum.RepeatDays || interval) {
      const days = interval || 1;
      return {
        label: days === 1 ? 'Every day' : `Every ${days} days`,
        pattern: RecurrenceTypeEnum.RepeatDays,
        intervalDays: days,
        isRecurring: true,
      };
    }

    return { label: 'One-time', pattern: RecurrenceTypeEnum.None, intervalDays: 0, isRecurring: false };
  }, [eventAttrs]);

  const localTimezone = useMemo(() => moment.tz.guess(), []);
  const localTimezoneDisplay = useMemo(
    () => formatEventTimezoneDisplay(localTimezone, eventAttrs?.startTime),
    [localTimezone, eventAttrs],
  );
  const showOrganizerTime = Boolean(eventTimezone) && eventTimezone !== localTimezone;

  /**
   * Both readings of the same instant: the viewer's timezone first, the organizer's
   * below. For a recurring series the stored endTime is the last occurrence, so it is
   * pinned onto the start date — a single occurrence begins and ends the same day.
   */
  const dateTimeDisplay = useMemo(() => {
    if (!eventAttrs?.startTime) {
      return null;
    }

    const start = eventTimezone ? moment.tz(eventAttrs.startTime, eventTimezone) : moment(eventAttrs.startTime);
    let end = eventAttrs.endTime
      ? (eventTimezone ? moment.tz(eventAttrs.endTime, eventTimezone) : moment(eventAttrs.endTime))
      : null;

    if (end && recurrence.isRecurring) {
      end = end.clone().year(start.year()).month(start.month()).date(start.date());
    }

    return {
      local: { start: start.clone().tz(localTimezone), end: end && end.clone().tz(localTimezone) },
      organizer: { start, end },
    };
  }, [eventAttrs, eventTimezone, localTimezone, recurrence]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), LIVE_CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  /** The meeting is joinable from shortly before the current occurrence until it ends. */
  const isEventLive = now ? true : false;
  // const isEventLive = useMemo(() => {
  //   if (!eventAttrs?.startTime) {
  //     return false;
  //   }

  //   const currentTime = eventTimezone ? moment.tz(now, eventTimezone) : moment(now);
  //   const start = eventTimezone ? moment.tz(eventAttrs.startTime, eventTimezone) : moment(eventAttrs.startTime);
  //   const seriesEnd = eventAttrs.endTime
  //     ? (eventTimezone ? moment.tz(eventAttrs.endTime, eventTimezone) : moment(eventAttrs.endTime))
  //     : start.clone().add(1, 'hour');

  //   if (!recurrence.isRecurring) {
  //     return currentTime.isBetween(start.clone().subtract(JOIN_LEAD_MINUTES, 'minutes'), seriesEnd, undefined, '[]');
  //   }

  //   // Outside the series window entirely.
  //   if (currentTime.isBefore(start, 'day') || currentTime.isAfter(seriesEnd, 'day')) {
  //     return false;
  //   }

  //   const weekday = currentTime.day();
  //   const isWeekend = weekday === 0 || weekday === 6;

  //   if (recurrence.pattern === RecurrenceTypeEnum.Weekdays && isWeekend) {
  //     return false;
  //   }

  //   if (recurrence.pattern === RecurrenceTypeEnum.Weekends && !isWeekend) {
  //     return false;
  //   }

  //   if (recurrence.pattern === RecurrenceTypeEnum.RepeatDays && recurrence.intervalDays > 1) {
  //     const daysSinceStart = currentTime.clone().startOf('day').diff(start.clone().startOf('day'), 'days');
  //     if (daysSinceStart % recurrence.intervalDays !== 0) {
  //       return false;
  //     }
  //   }

  //   const occurrenceStart = currentTime
  //     .clone()
  //     .set({ hour: start.hour(), minute: start.minute(), second: 0, millisecond: 0 });
  //   const occurrenceEnd = currentTime
  //     .clone()
  //     .set({ hour: seriesEnd.hour(), minute: seriesEnd.minute(), second: 0, millisecond: 0 });

  //   if (occurrenceEnd.isSameOrBefore(occurrenceStart)) {
  //     occurrenceEnd.add(1, 'day');
  //   }

  //   return currentTime.isBetween(
  //     occurrenceStart.clone().subtract(JOIN_LEAD_MINUTES, 'minutes'),
  //     occurrenceEnd,
  //     undefined,
  //     '[]',
  //   );
  // }, [eventAttrs, eventTimezone, now, recurrence]);

  const capacity = eventAttrs?.capacity;
  const registeredCount = attendants.length;
  const spotsLeft = eventAttrs?.remainingSeats;

  const [endMeeting, { loading: endMeetingLoading }] = useEndMeetingLazyQuery();

  const handleEndMeeting = useCallback(async () => {
    if (!id) return;
    try {
      await endMeeting({ variables: { eventId: id } });
      message.success('Meeting ended successfully');
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  }, [endMeeting, id]);

  const [updateEventDetails, { loading: updateEventLoading }] = useUpdateEventAtOnceMutation({
    refetchQueries: [namedOperations.Query.GetEventById],
  });

  // Creates the Meet on the organizer's own Google Calendar, then stores the link on the event.
  const handleGenerateGoogleMeet = useCallback(async () => {
    if (!id) return;
    try {
      const accessToken = await requestGoogleAccessToken(GOOGLE_CALENDAR_EVENTS_SCOPE);
      const { startTime, endTime } = resolveMeetingWindow(eventAttrs?.startTime, eventAttrs?.endTime, eventTimezone);

      const { hangoutLink } = await createGoogleMeetEvent({
        accessToken,
        summary: eventAttrs?.title || 'Polgozār event',
        startTime,
        endTime,
        timezone: eventTimezone || moment.tz.guess(),
      });

      await updateEventDetails({
        variables: {
          eventId: id,
          input: {
            eventUrl: hangoutLink,
            virtualMeetingType: VirtualMeetingTypeEnum.OwnLink,
            // A physical-only event becomes hybrid so the join link is actually shown.
            ...(eventAttrs?.eventType === EventTypeEnum.Physical ? { eventType: EventTypeEnum.Both } : {}),
          },
        },
      });

      message.success('Google Meet created and added to your calendar.');
    } catch (err) {
      if (err instanceof Error && err.message !== 'access_denied') {
        message.error(err.message || 'Failed to create the Google Meet.');
      }
    }
  }, [id, eventAttrs, eventTimezone, updateEventDetails]);

  const handleEditEvent = useCallback(() => {
    history.push(`/events/update/${id}`);
  }, [history, id]);

  const handleManageBookings = useCallback(() => {
    history.push(`/events/bookings/${id}`);
  }, [history, id]);

  const [donateModalVisible, setDonateModalVisible] = useState(false);
  const [meetingModalVisible, setMeetingModalVisible] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);

  const renderAttendeeName = (attendee: {
    firstname?: string;
    lastname?: string;
    username?: string;
    email?: string;
    isVisitor?: boolean;
  }) => {
    if (attendee.isVisitor) {
      return attendee.email || 'Visitor';
    }
    const fullname = `${attendee.firstname || ''} ${attendee.lastname || ''}`.trim();
    return fullname || attendee.username || 'Unknown user';
  };

  const renderAttendeeSubtitle = (
    attendee: {
      username?: string;
      email?: string;
      isVisitor?: boolean;
    },
    fallback: string,
  ) => {
    if (attendee.isVisitor) {
      return attendee.email || 'Visitor';
    }
    return attendee.username || fallback;
  };

  const toPeopleItems = (people: typeof bookedPeople, subtitleFallback: string) =>
    people.map((person) => ({
      id: person.id,
      name: renderAttendeeName(person),
      subtitle: renderAttendeeSubtitle(person, subtitleFallback),
      avatarUrl: person.avatarUrl,
    }));

  const isMobileLayout = useMediaQuery(`(max-width: ${breakpoints.xl}px)`);

  const heroBlock = (
    <HeroCard coverPhotoUrl={eventAttrs?.coverPhotoUrl || ''}>
      <HeroInfo>
        <HeroTitle>{eventAttrs?.title || 'Event'}</HeroTitle>
        <HeroMeta>
          <span>
            <i className="icon-pin" /> {location}
          </span>
          <span>
            <i className="icon-user" /> {attendants.length} attendees
          </span>
        </HeroMeta>
      </HeroInfo>
      {isEventType(EventTypeEnum.Virtual, eventAttrs?.eventType) && eventAttrs?.eventUrl ? (
        <>
          <HeroCenteredJoin>
            {isEventLive ? (
              <HeroActionLink href={eventAttrs?.eventUrl || '#'} target="_blank" rel="noopener noreferrer">
                Join Meeting Now
                <ArrowRightOutlined />
              </HeroActionLink>
            ) : (
              <HeroJoinDisabled
                aria-disabled="true"
                title={`Available ${JOIN_LEAD_MINUTES} minutes before the event starts`}
              >
                Join Meeting Now
                <ArrowRightOutlined />
              </HeroJoinDisabled>
            )}
          </HeroCenteredJoin>
          {isEventOrganizer && (
            <HeroActions>
              <HeroActionButton type="default" loading={endMeetingLoading} onClick={handleEndMeeting}>
                <CloseCircleOutlined />
                End Meeting
              </HeroActionButton>
            </HeroActions>
          )}
        </>
      ) : (
        isEventOrganizer && (
          <HeroActions>
            <HeroActionButton type="default" onClick={() => setMeetingModalVisible(true)}>
              <CustomerServiceOutlined />
              Generate Zoom Link
            </HeroActionButton>
            <HeroActionButton type="default" loading={updateEventLoading} onClick={handleGenerateGoogleMeet}>
              <VideoCameraOutlined />
              Generate Google Meet
            </HeroActionButton>
          </HeroActions>
        )
      )}
    </HeroCard>
  );

  const dateTimeBlock = dateTimeDisplay && (
    <DateTimeSection>
      <DateTimeRow>
        <DatePill>
          <DatePillDate>{dateTimeDisplay.local.start.format(DATE_FORMAT)}</DatePillDate>
          <DatePillTime>{dateTimeDisplay.local.start.format(TIME_FORMAT)}</DatePillTime>
        </DatePill>
        {dateTimeDisplay.local.end && (
          <>
            <DateArrow aria-hidden>»</DateArrow>
            <DatePill>
              <DatePillDate>{dateTimeDisplay.local.end.format(DATE_FORMAT)}</DatePillDate>
              <DatePillTime>{dateTimeDisplay.local.end.format(TIME_FORMAT)}</DatePillTime>
            </DatePill>
          </>
        )}
      </DateTimeRow>
      <DateTimeZoneNote>Your time zone{localTimezoneDisplay ? ` — ${localTimezoneDisplay}` : ''}</DateTimeZoneNote>

      {showOrganizerTime && (
        <>
          <SecondaryDateTimeRow>
            <SecondaryDatePill>
              <SecondaryDatePillDate>{dateTimeDisplay.organizer.start.format(DATE_FORMAT)}</SecondaryDatePillDate>
              <SecondaryDatePillTime>{dateTimeDisplay.organizer.start.format(TIME_FORMAT)}</SecondaryDatePillTime>
            </SecondaryDatePill>
            {dateTimeDisplay.organizer.end && (
              <>
                <DateArrow aria-hidden>»</DateArrow>
                <SecondaryDatePill>
                  <SecondaryDatePillDate>{dateTimeDisplay.organizer.end.format(DATE_FORMAT)}</SecondaryDatePillDate>
                  <SecondaryDatePillTime>{dateTimeDisplay.organizer.end.format(TIME_FORMAT)}</SecondaryDatePillTime>
                </SecondaryDatePill>
              </>
            )}
          </SecondaryDateTimeRow>
          <DateTimeZoneNote>Event time zone — {eventTimezoneDisplay}</DateTimeZoneNote>
        </>
      )}
    </DateTimeSection>
  );

  const aboutBlock = (
    <SurfaceCard>
      <SectionTitleRow>
        <SectionTitle>About This Event</SectionTitle>
        {isEventOrganizer ? (
          <Button type="primary" onClick={handleEditEvent}>
            <EditOutlined />
            Edit Event
          </Button>
        ) : null}
      </SectionTitleRow>
      {dateTimeBlock}
      <RichText>{ReactHtmlParser(eventAttrs?.description || '')}</RichText>
    </SurfaceCard>
  );

  const dailyMeetingNotesBlock = (
    <DailyMeetingNotes
      eventId={id}
      eventStartTime={eventAttrs?.startTime}
      canEdit={isEventOrganizer}
      passcode={eventAttrs?.passcode}
    />
  );

  const albumBlock = userId && (
    <DividerCard>
      <SingleEventAlbum
        gallery={(eventAttrs?.gallery || []) as ComponentMediaGallery[]}
        removePhoto={userId && removePhoto}
        onImport={userId && uploadPhoto}
        approvePhoto={userId && approvePhoto}
        uploadLoading={uploadPhotoLoading}
        removeLoading={removePhotoLoading}
        isEventOrganizer={isEventOrganizer}
        userId={userId}
      />
    </DividerCard>
  );

  const relatedBlock = (
    <DividerCard>
      <SliderList
        renderList={(item) => <EventCard {...item.attributes} />}
        title="Related Events"
        items={eventsResult?.data?.events?.data || []}
      />
    </DividerCard>
  );

  const detailsAndAttendees = (
    <>
      <EventDetailsCard>
        <SideTitle>Event Details</SideTitle>

        <LocationBlock>
          <LocationPin>
            <EnvironmentOutlined />
          </LocationPin>
          <LocationText>
            <VenueName>{venueName}</VenueName>
            {addressLine ? <AddressLine>{addressLine}</AddressLine> : null}
          </LocationText>
        </LocationBlock>

        <StatRows>
          <DetailRow>
            <DetailLabel>Recurring Type</DetailLabel>
            <DetailValue>{recurrence.label}</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailLabel>Capacity</DetailLabel>
            <DetailValue>{capacity ? `${capacity} people` : '—'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Invited</DetailLabel>
            <DetailValue>{registeredCount}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Spots Left</DetailLabel>
            <DetailValue>{spotsLeft != null ? spotsLeft : '—'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Price</DetailLabel>
            <DetailValue>{eventAttrs?.price ? `$${formatMoney(eventAttrs.price)}` : 'Free'}</DetailValue>
          </DetailRow>
        </StatRows>

        {event?.id && (
          <RegistrationPanel>
            <RsvpEventForm eventId={event.id} remainingSeats={eventAttrs?.remainingSeats} />
          </RegistrationPanel>
        )}

        <SocialMedia
          onDonate={() => setDonateModalVisible(true)}
          onInvite={() => setInviteModalVisible(true)}
          onManageBookings={isEventOrganizer && !eventAttrs?.isPast ? handleManageBookings : undefined}
        />
      </EventDetailsCard>

      <PeopleListCard
        title="Attendees"
        meta={`${attendants.length} approved`}
        items={toPeopleItems(attendants, 'Attendee')}
        maxItems={PEOPLE_LIST_PREVIEW_COUNT}
      />

      {invitees.length > 0 && (
        <PeopleListCard
          title="Invitees"
          meta={`${invitees.length} pending`}
          items={toPeopleItems(invitees, 'Pending')}
          maxItems={PEOPLE_LIST_PREVIEW_COUNT}
        />
      )}
    </>
  );

  return (
    <PageWrapper>
      <Modal
        open={donateModalVisible}
        onCancel={() => {
          setDonateModalVisible(false);
        }}
      >
        <DonateForm
          paypalEmail="admin@polgozar.org"
          stripePublishableKey={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''}
          onFormSubmitted={() => setDonateModalVisible(false)}
          onApplePaySuccess={() => setDonateModalVisible(false)}
        />
      </Modal>
      <InvitationModal
        modalVisible={inviteModalVisible}
        setModal={setInviteModalVisible}
        eventId={id}
        eventTitle={eventAttrs?.title}
      />
      <MeetingModal
        modalVisible={meetingModalVisible}
        setModal={setMeetingModalVisible}
        eventId={id}
        eventStartTime={eventAttrs?.startTime}
        eventEndTime={eventAttrs?.endTime}
      />
      <PageLayout>
        <BackButton type="button" onClick={() => history.goBack()}>
          <ArrowLeftOutlined />
          Back
        </BackButton>

        {isMobileLayout ? (
          <MobileGrid>
            {heroBlock}
            {aboutBlock}
            {dailyMeetingNotesBlock}
            {detailsAndAttendees}
            {albumBlock}
            {relatedBlock}
          </MobileGrid>
        ) : (
          <>
            <MainGrid>
              <LeftColumn>
                {heroBlock}
                {aboutBlock}
                {dailyMeetingNotesBlock}
                {albumBlock}
              </LeftColumn>
              <RightColumn>{detailsAndAttendees}</RightColumn>
            </MainGrid>
            {relatedBlock}
          </>
        )}
      </PageLayout>
    </PageWrapper>
  );
}
